import { OrganizationRole } from "../../common/models/organization-role";
import { UtilsProvider } from "../../common/providers/utils-provider.service";
import { AthleteCoach } from "../../common/models/athlete-coach";
import { User } from "../../common/models/user";
import { OrganizationMembership } from "../../common/models/organization-membership";
import { Athlete } from "../../common/models/athlete";
import { Coach } from "../../common/models/coach";
import { SessionItemRightsList } from "../../sessions/providers/session-rights";
import { Feedback } from "../../common/models/feedback";

export class FeedbackRights {
    public Ready: Promise<boolean> = new Promise((resolve, reject) => resolve(false));
    private _itemRightsList: FeedbackItemRightsList | null = null;
    private _screenRightsList: FeedbackScreenRightList | null = null;
    private _paramsItem: FeedbackItemRightsParams | null = null;
    private _paramsScreen: FeedbackScreenRightsParams | null = null;
    private _bestOfItemRight = 10000;
    private _bestOfScreenRight = 10000;
    constructor(private _utils: UtilsProvider) {
        this.setDefault();
    }

    public evaluateItemRight(params: FeedbackItemRightsParams): void {

        /**
         * Les paramètres reçus contiennent le feedback, le user.id
         * et le organizationId. Les droits évalués doivent répondrent
         * à la question : qu'est-ce que je (user.id) peux faire avec
         * ce feedback (FeedbackAthleteAnswer.id) dans cette organisation (organizationId)
         * avec mon rôle (this._bestOfItemRight).
         */

        this._paramsItem = params;
        this.setDefault();
        this.setBestOfRole();

        const athlete = this._paramsItem.athlete;
        const memberships = this._utils.findManyIn([athlete.userId], this._paramsItem.athleteMemberships, 'userId');
        const orgIds = this._utils.getKeyValues(memberships, 'organizationId');

        if (!this._itemRightsList) throw new Error('ItemRightsList is null');
        switch (this._bestOfItemRight) {
        case OrganizationRole.ID_ADMIN:
            // this._paramsItem.user est un admin
            // tous les feedbacks
            this._itemRightsList.view = true;
            this._itemRightsList.edit = true;
            this._itemRightsList.delete = true;
            break;

        case OrganizationRole.ID_SUPERCOACH:
            // this._paramsItem.user est un supercoach
            // tous les feedbacks de son organisation
            if (orgIds.indexOf(this._paramsItem.organizationId) > -1) {
                this._itemRightsList.view = true;
                this._itemRightsList.edit = true;
                this._itemRightsList.delete = true;
            }
            break;

        case OrganizationRole.ID_COACH:
            // this._paramsItem.user est un coach
            // tous les feedbacks de ses athlètes
            const links = this._utils.findManyIn([athlete.id], this._paramsItem.athleteCoachRelations, 'athleteId');
            const coachIds = this._utils.getKeyValues(links, 'coachId');
            const me = this._utils.findIn(this._paramsItem.user.id, this._paramsItem.coaches, 'userId');

            if (coachIds.indexOf(me.id) > -1) {
                this._itemRightsList.view = true;
                this._itemRightsList.edit = true;
                this._itemRightsList.delete = true;
            }
            break;

        case OrganizationRole.ID_ATHLETE:
            // this._paramsItem.user est un athlète
            // tous ses feedbacks
            this._itemRightsList.view = (this._paramsItem.user.id === athlete.userId);
            this._itemRightsList.edit = (this._paramsItem.user.id === athlete.userId);
            this._itemRightsList.delete = (this._paramsItem.user.id === athlete.userId);
            break;

        case OrganizationRole.ID_GUEST:
            // this._paramsItem.user est un invité
            // aucun
            break;

        default:
            // rien
        }
    }

    public evaluateScreenRights(params: FeedbackScreenRightsParams): void {

        /**
         * Détermine les droits que l'utilisateur possède
         * pour les commandes générales des écrans.
         */

        this._paramsScreen = params;
        this.setDefault();
        this.setBestOfRole();

        if(!this._screenRightsList) throw new Error('ScreenRightsList is null');
        switch (this._bestOfScreenRight) {
        case OrganizationRole.ID_ADMIN:
            // this._paramsItem.user est un admin
            this._screenRightsList.list = true;
            this._screenRightsList.add = true;
            this._screenRightsList.edit = true;
            this._screenRightsList.delete = true;
            break;

        case OrganizationRole.ID_SUPERCOACH:
            // this._paramsItem.user est un super-coach
            this._screenRightsList.list = true;
            this._screenRightsList.add = true;
            this._screenRightsList.edit = true;
            this._screenRightsList.delete = true;
            break;

        case OrganizationRole.ID_COACH:
            // this._paramsItem.user est un coach
            this._screenRightsList.list = true;
            this._screenRightsList.add = true;
            this._screenRightsList.edit = true;
            this._screenRightsList.delete = true;
            break;

        case OrganizationRole.ID_ATHLETE:
            // this._paramsItem.user est un athlète
            this._screenRightsList.list = true;
            this._screenRightsList.add = true;
            this._screenRightsList.edit = true;
            this._screenRightsList.delete = true;
            break;

        case OrganizationRole.ID_GUEST:
            // this._paramsItem.user est un invité
            this._screenRightsList.list = false;
            this._screenRightsList.add = false;
            this._screenRightsList.edit = false;
            this._screenRightsList.delete = false;
            break;

        default:
            // rien
        }
    }

    public getItemRightsList(): FeedbackItemRightsList {
        if(!this._itemRightsList) throw new Error('ItemRightsListghtsList is null');
        return this._itemRightsList;
    }

    public setItemRightsList(feedback: Feedback): void {
        this._itemRightsList = mapFeedbackItemRightsToRowRights(feedback.rights ?? []);
    }

    public getScreenRightsList(): FeedbackScreenRightList {
        if(!this._screenRightsList) throw new Error('ScreenRightsList is null');
        return this._screenRightsList;
    }

    public getItemParams(): FeedbackItemRightsParams {
        if(!this._paramsItem) throw new Error('ParamsItem is null');
        return this._paramsItem;
    }

    public getScreenParams(): FeedbackScreenRightsParams {
        if(!this._paramsScreen) throw new Error('ParamsScreen is null');
        return this._paramsScreen;
    }

    public get LIST() {
        if (this._screenRightsList) {
            return this._screenRightsList.list;
        }
        return false;
    }

    public get ADD() {
        if (this._screenRightsList) {
            return this._screenRightsList.add;
        }
        return false;
    }

    public get EDIT() {
        if (this._screenRightsList) {
            return this._screenRightsList.edit;
        }
        return false;
    }

    public get DELETE() {
        if (this._screenRightsList) {
            return this._screenRightsList.delete;
        }
        return false;
    }

    private setDefault(): void {

        const defaultItemRights: FeedbackItemRightsList = {
            view: false,
            edit: false,
            delete: false,
        };
        this._itemRightsList = defaultItemRights;

        const defaultScreenRights: FeedbackScreenRightList = {
            list: false,
            add: false,
            edit: false,
            delete: false,
        };
        this._screenRightsList = defaultScreenRights;

        this._bestOfItemRight = 10000;
        this._bestOfScreenRight = 10000;
    }

    private setBestOfRole() {

        if (this._paramsItem && this._paramsItem.rolesIds.length > -1) {
            for (let i = 0; i < this._paramsItem.rolesIds.length; i++) {
                if (this._paramsItem.rolesIds[i] < this._bestOfItemRight) {
                    this._bestOfItemRight = this._paramsItem.rolesIds[i];
                }
            }
        }

        if (this._paramsScreen && this._paramsScreen.rolesIds.length > -1) {
            for (let i = 0; i < this._paramsScreen.rolesIds.length; i++) {
                if (this._paramsScreen.rolesIds[i] < this._bestOfScreenRight) {
                    this._bestOfScreenRight = this._paramsScreen.rolesIds[i];
                }
            }
        }
    }
}

export interface FeedbackItemRightsList {
    view: boolean;
    edit: boolean;
    delete: boolean;
}

const defaultRights: FeedbackItemRightsList = {
    view: false,
    edit: false,
    delete: false,
};

export function mapFeedbackItemRightsToRowRights(feedbackRights: string []): FeedbackItemRightsList {
  const rights = { ...defaultRights };

  feedbackRights.forEach((right: string) => {
    if (right in rights) {
      rights[right as keyof FeedbackItemRightsList] = true;
    }
  });

  return rights;
}

export interface FeedbackScreenRightList {
    list: boolean;
    add: boolean;
    edit: boolean;
    delete: boolean;
}

export interface FeedbackItemRightsParams {
    user: User;
    organizationId: number;
    // feedback: Feedback;
    rolesIds: number[];
    athleteCoachRelations: AthleteCoach[];
    athleteMemberships: OrganizationMembership[];
    athletes: Athlete[];
    coaches: Coach[];
    athlete: Athlete;
}

export interface FeedbackScreenRightsParams {
    rolesIds: number[];
}
